import { CountryService } from '@/services/country.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  ViewEncapsulation,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { CountryISO2 } from '@edt/models/checkout';
import { CountryNamePipe } from '@edt/pipes/translate-country-name.pipe';
import { timer } from 'rxjs';

@Component({
  selector: 'store-phone-input',
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatAutocompleteModule,
    CountryNamePipe,
  ],
  templateUrl: './phone-input.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    store-phone-input .mat-mdc-select-trigger {
      flex-direction: row-reverse;
      padding-left: 8px;
    }
  `,
})
export class PhoneInputComponent implements AfterViewInit, OnInit {
  private countryService = inject(CountryService);
  private _destroyRef = inject(DestroyRef);
  private _iso2Name = inject(CountryNamePipe);
  private _phoneInputRef =
    viewChild<ElementRef<HTMLInputElement>>('phoneInput');
  private _countrySelectRef = viewChild(MatSelect);

  disabled = input<boolean>(false);
  countries = input<CountryISO2[]>([]);
  countryFormControl = input.required<FormControl<string>>();
  phoneFormControl = input.required<FormControl<string>>();
  initialCountryCode = input<string>('cu');

  protected countriesList = computed(() => {
    return this.countries().length > 0
      ? this.countries()
      : this.countryService
          .countries()
          .map(country => ({
            iso_2: country.cca2,
            flag: country.flag,
          }))
          .sort((a, b) =>
            this._iso2Name
              .transform(a.iso_2, 'es')
              .localeCompare(this._iso2Name.transform(b.iso_2, 'es'))
          );
  });
  protected filteredCountries = signal<CountryISO2[]>([]);
  protected dialCode = signal<string>('');

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.countryFormControl().disable();
        this.phoneFormControl().disable();
      } else {
        this.countryFormControl().enable();
        this.phoneFormControl().enable();
      }
    });

    this.filteredCountries.set(this.countriesList().slice());
  }

  async ngOnInit() {
    await this.countryService.ensureCountriesLoaded();
  }

  ngAfterViewInit(): void {
    //Update countryFormControl value
    this._countrySelectRef()
      ?.valueChange.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((code: string) => {
        this.countryFormControl().setValue(code);
        this.countryFormControl().updateValueAndValidity();
      });

    //Update dialCode value
    this.countryFormControl()
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(code => {
        const country = this.countryService.getCountryByCode(code);
        const idd = country?.idd;
        this.dialCode.set(idd ? idd.root + idd.suffixes[0] : '');
      });

    if (this._countrySelectRef()) {
      //Set select initial country
      const countryCode = this.initialCountryCode().toUpperCase();
      this._countrySelectRef()!.writeValue(countryCode);
      this.countryFormControl().setValue(countryCode);
    }
  }

  protected onSelectCountryOpenedChange(event: boolean) {
    timer(100).subscribe(() => {
      if (event) {
        this._countrySelectRef()!.focus();
      } else {
        this._phoneInputRef()!.nativeElement.focus();
      }
    });
  }

  displayCountryName = (isoCode: string): string => {
    return isoCode ? this._iso2Name.transform(isoCode, 'es') : '';
  };
}
